// Client multi-matter list: hero + tabbed activity strip.
//
// The hero card sits above pill-style tabs - Active matters (default),
// Closed matters, Relatives, Judges, Opposing counsel, then the tabs the
// modules register. The selected tab is mirrored to a `?tab=` query param so
// back-navigation lands on the same tab the user was on. Active reads the
// client's own matters; Closed, Relatives, Judges and Opposing counsel each
// fetch `/api/clients/:id/{slice}` via TanStack Query, and a failed slice
// falls back to an empty array (no crash) so the prototype/preview data path
// keeps working; a module's tab answers for itself. Every list answers from
// the matters in the viewer's scope.

import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { practiceAreaById, practiceAreaLabel } from "../../data/practice-areas";
import { caseKindLabel, stageLabel } from "../../lib/caseLabels";
import { languageName } from "../../lib/intakeLanguages";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { EMPTY, SEP, SepList } from "../chrome/SepList";
import { ClientSignals } from "./ClientSignals";
import { useClient } from "../../hooks/useClient";
import {
  fetchClientClosedMatters,
  fetchClientJudges,
  fetchClientOpposingCounsel,
  fetchClientRelatives,
  type ClientJudge,
  type ClientOpposingCounsel,
  type ClientRelative,
  type ClientWithMatters,
} from "../../api/mock";
import type { Matter } from "../../data/types";
import { recordStep, usePageTrail } from "../chrome/trail";
import { FixtureNotice } from "../chrome/FixtureNotice";
import { useScreens } from "../chrome/screens";
import { EntityLink } from "../chrome/EntityLink";
import { EmptyCopy } from "../chrome/EmptyCopy";

type TabKey = "active" | "closed" | "relatives" | "judges" | "opposing";

const FIXTURE_NOTICE: Record<string, string> = {
  missing: "This client is not in the firm database - showing prototype data.",
  unreachable: "Could not reach the server - showing prototype data.",
};

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "active", label: "Active matters" },
  { key: "closed", label: "Closed matters" },
  { key: "relatives", label: "Relatives" },
  { key: "judges", label: "Judges" },
  { key: "opposing", label: "Opposing counsel" },
];

function isValidTab(s: string | null | undefined, tabs: Array<{ key: string }>): s is string {
  return !!s && tabs.some((t) => t.key === s);
}

// Prefill for the matter search: an identifier, never the name - two
// clients can share a name, and a wrong prefilled hit files money against
// the wrong person. With no identifier the dialog opens on an empty box.
function lookupKey(c: ClientWithMatters): string | undefined {
  return c.alienNumber || c.contactEmail || c.contactPhone || undefined;
}

export function ClientView() {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: c, isLoading, isError } = useClient(clientId);
  usePageTrail([{ label: "Clients", to: "/clients" }, recordStep(c?.name, isLoading, "Client")]);
  const screens = useScreens();
  const moduleTabs = screens.flatMap((s) => s.clientTabs ?? []);
  const tabs = [...TABS, ...moduleTabs];
  const actions = screens.flatMap((s) =>
    (s.clientActions ?? []).map((Action, i) => ({ key: `${s.id}-${i}`, Action })),
  );

  const tabFromUrl = searchParams.get("tab");
  const activeTab = isValidTab(tabFromUrl, tabs) ? tabFromUrl : "active";
  const ModulePanel = moduleTabs.find((t) => t.key === activeTab)?.panel;

  const setTab = (k: string) => {
    // Persist in URL without thrashing browser history — replaceState so
    // back-navigation skips the intermediate tab clicks.
    const next = new URLSearchParams(searchParams);
    if (k === "active") next.delete("tab");
    else next.set("tab", k);
    setSearchParams(next, { replace: true });
  };

  if (isLoading) {
    return (
      <div style={{ padding: 40 }}>
        <SkeletonGroup label="Loading client">
          <Skeleton width="30%" height={22} />
          <Skeleton width="100%" height={84} radius={10} />
          <Skeleton width="100%" height={160} radius={10} />
        </SkeletonGroup>
      </div>
    );
  }
  if (isError) {
    // The record's existence is unknown when the API is down - saying
    // "not found" here would be a false claim about the firm's data.
    return (
      <div style={{ padding: 40 }}>
        <div className="cc-card" style={{ padding: "10px 18px", color: "var(--warn-600)" }}>
          Could not reach the server - the client record is unavailable.
        </div>
      </div>
    );
  }
  if (!c) return <div style={{ padding: 40 }}>Client not found.</div>;

  const activeMatters = c.matters.filter((m) => m.status !== "closed");

  const heroSub: ReactNode[] = [];
  if (c.alienNumber) {
    heroSub.push(
      <span key="a" className="tnum-mono">
        {c.alienNumber}
      </span>,
    );
  }
  if (c.status && c.status !== "active") heroSub.push(c.status);
  if (c.dateOfBirth) heroSub.push("DOB " + c.dateOfBirth);
  if (c.primaryLanguage) heroSub.push(languageName(c.primaryLanguage));
  if (c.contactPhone) {
    heroSub.push(
      <a key="p" href={`tel:${c.contactPhone}`} className="cc-link">
        {c.contactPhone}
      </a>,
    );
  }
  if (c.contactEmail) {
    heroSub.push(
      <a key="e" href={`mailto:${c.contactEmail}`} className="cc-link">
        {c.contactEmail}
      </a>,
    );
  }

  return (
    <div className="main">
      <div className="detail-body">
        {c.fixtures && <FixtureNotice>{FIXTURE_NOTICE[c.fixtures]}</FixtureNotice>}

        <div
          className="cc-card"
          style={{ padding: 18, flexDirection: "row", alignItems: "flex-start", gap: 12 }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink)" }}>{c.name}</div>
            {heroSub.length > 0 && (
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                <SepList parts={heroSub} />
              </div>
            )}
          </div>
          {/* Keyed by the client, like the matter panels: a cached client
              renders without a skeleton in between, and one client's typed
              state must not land on the next. */}
          {actions.map(({ key, Action }) => (
            <Action key={`${key}-${c.id}`} lookupKey={lookupKey(c)} fixtures={!!c.fixtures} />
          ))}
        </div>

        <ClientSignals clientId={c.id} />

        <div className="client-tabs-sticky">
          <div
            className="client-tabs"
            role="tablist"
            aria-label="Client sections"
            data-active-tab={activeTab}
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={activeTab === t.key}
                className={activeTab === t.key ? "active" : undefined}
                onClick={() => setTab(t.key)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="client-tab-body" role="tabpanel">
          {activeTab === "active" && <ActiveMattersTab matters={activeMatters} />}
          {activeTab === "closed" && <ClosedMattersTab clientId={c.id} />}
          {activeTab === "relatives" && <RelativesTab clientId={c.id} />}
          {activeTab === "judges" && <JudgesTab clientId={c.id} />}
          {activeTab === "opposing" && <OpposingCounselTab clientId={c.id} />}
          {ModulePanel && <ModulePanel key={c.id} clientId={c.id} fixtures={!!c.fixtures} />}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Tab components — each one fetches its own slice via TanStack Query.
// ──────────────────────────────────────────────────────────────────────

function MatterRow({ m }: { m: Matter }) {
  const navigate = useNavigate();
  const pa = practiceAreaById(m.practiceArea);
  const kind = caseKindLabel(m);
  return (
    <div className="row clickable" onClick={() => navigate(`/matter/${m.eID}`)}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="pa-dot lg" style={{ background: pa?.accent }} />
        <div>
          <div className="row-title">
            {pa ? (
              <EntityLink kind="practiceArea" id={pa.id} stopPropagation subtle>
                {pa.label}
              </EntityLink>
            ) : (
              practiceAreaLabel(m.practiceArea)
            )}
            {kind ? SEP + kind : ""}
          </div>
          <div className="row-sub">{stageLabel(m.stage)}</div>
        </div>
      </div>
      <div className="row-meta">
        <EntityLink
          kind="matter"
          id={m.eID}
          eID={m.eID}
          stopPropagation
          subtle
          className="tnum-mono"
        >
          {m.eID}
        </EntityLink>
        <div className="row-meta-sub">opened {m.opened}</div>
      </div>
    </div>
  );
}

function ActiveMattersTab({ matters }: { matters: Matter[] }) {
  if (matters.length === 0) {
    return (
      <div className="cc-empty">
        <div className="icon" aria-hidden>
          ☼
        </div>
        <EmptyCopy
          title="No active matters"
          mine="No active matters assigned to you"
          body="When this client has an open case, it will show up here. Closed matters live on the next tab."
        />
      </div>
    );
  }
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Active matters</div>
        <div className="cc-card-sub">· {matters.length}</div>
      </div>
      <div>
        {matters.map((m) => (
          <MatterRow key={m.eID} m={m} />
        ))}
      </div>
    </div>
  );
}

function ClosedMattersTab({ clientId }: { clientId: string }) {
  const { data: matters = [], isLoading } = useQuery<Matter[]>({
    queryKey: ["client", clientId, "closed-matters"],
    queryFn: () => fetchClientClosedMatters(clientId),
  });

  if (isLoading) return <div className="client-tab-empty">Loading…</div>;
  if (matters.length === 0) {
    return (
      <div className="cc-empty">
        <div className="icon" aria-hidden>
          ◍
        </div>
        <EmptyCopy
          title="No closed matters"
          mine="No closed matters assigned to you"
          body="Once a matter wraps up and is marked closed, it gets archived here so the active list stays focused."
        />
      </div>
    );
  }
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Closed matters</div>
        <div className="cc-card-sub">· {matters.length}</div>
      </div>
      <div>
        {matters.map((m) => (
          <MatterRow key={m.eID} m={m} />
        ))}
      </div>
    </div>
  );
}

function RelativesTab({ clientId }: { clientId: string }) {
  const { data: relatives = [], isLoading } = useQuery<ClientRelative[]>({
    queryKey: ["client", clientId, "relatives"],
    queryFn: () => fetchClientRelatives(clientId),
  });

  if (isLoading) return <div className="client-tab-empty">Loading…</div>;
  if (relatives.length === 0) {
    return (
      <div className="cc-empty">
        <div className="icon" aria-hidden>
          ✿
        </div>
        <EmptyCopy
          title="No relatives recorded yet"
          mine="No relatives on the matters assigned to you"
          body="Family-member entries from any of this client's matter intakes show up here, deduped across all matters."
        />
      </div>
    );
  }
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Relatives</div>
        <div className="cc-card-sub">· {relatives.length}</div>
      </div>
      <div>
        {relatives.map((r) => (
          <RelativeRow key={`${r.caseId}:${r.index}`} r={r} />
        ))}
      </div>
    </div>
  );
}

function RelativeRow({ r }: { r: ClientRelative }) {
  const [open, setOpen] = useState(false);
  const fullName = useMemo(() => {
    const parts = [r.firstName, r.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "(unnamed)";
  }, [r.firstName, r.lastName]);

  return (
    <div
      className="client-relative-row"
      aria-expanded={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }}
    >
      <div className="head" onClick={() => setOpen((v) => !v)}>
        <div>
          <div className="row-title">{fullName}</div>
          <div className="row-sub">
            {r.relationship ?? EMPTY}
            {r.alienNumber ? (
              <>
                {SEP}A# <span className="tnum-mono">{r.alienNumber}</span>
              </>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="row-meta-sub">{open ? "Hide" : "Show"} details</div>
      </div>
      {open && (
        <dl className="details">
          <dt>Date of birth</dt>
          <dd className="tnum">{r.dateOfBirth ? String(r.dateOfBirth).slice(0, 10) : EMPTY}</dd>
          <dt>A-Number</dt>
          <dd className={r.alienNumber ? "tnum-mono" : undefined}>{r.alienNumber ?? EMPTY}</dd>
          <dt>In US</dt>
          <dd>{r.inUS ? "Yes" : r.inUS === false ? "No" : EMPTY}</dd>
          <dt>Status</dt>
          <dd>{r.status ?? EMPTY}</dd>
          <dt>From matter</dt>
          <dd className="tnum-mono">{r.caseEid ?? r.caseId}</dd>
        </dl>
      )}
    </div>
  );
}

function JudgesTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<{
    judges: ClientJudge[];
    unlinkedJudgeNames: string[];
  }>({
    queryKey: ["client", clientId, "judges"],
    queryFn: () => fetchClientJudges(clientId),
  });

  if (isLoading) return <div className="client-tab-empty">Loading…</div>;
  const judges = data?.judges ?? [];
  const unlinked = data?.unlinkedJudgeNames ?? [];
  if (judges.length === 0 && unlinked.length === 0) {
    return (
      <div className="cc-empty">
        <div className="icon" aria-hidden>
          ⚖
        </div>
        <EmptyCopy
          title="No judges recorded yet"
          mine="No judges on the matters assigned to you"
          body="Judges associated with any of this client's matters appear here."
        />
      </div>
    );
  }
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Judges</div>
        <div className="cc-card-sub">· {judges.length + unlinked.length}</div>
      </div>
      <div>
        {judges.map((j) => (
          <div key={j._id} className="row clickable" onClick={() => navigate(`/judge/${j._id}`)}>
            <div>
              <div className="row-title">
                <EntityLink kind="judge" id={j._id} stopPropagation subtle>
                  {j.name}
                </EntityLink>
              </div>
              <div className="row-sub">
                {[j.title, j.court, j.district].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            <div className="row-meta">
              {j.contactEmail ? (
                <a
                  href={`mailto:${j.contactEmail}`}
                  className="cc-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {j.contactEmail}
                </a>
              ) : null}
            </div>
          </div>
        ))}
        {unlinked.map((name) => (
          <div key={`u-${name}`} className="row">
            <div>
              <div className="row-title">{name}</div>
              <div className="row-sub">Free-text only — no Judge record yet</div>
            </div>
            <div className="row-meta-sub">unlinked</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OpposingCounselTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const { data: counsel = [], isLoading } = useQuery<ClientOpposingCounsel[]>({
    queryKey: ["client", clientId, "opposing-counsel"],
    queryFn: () => fetchClientOpposingCounsel(clientId),
  });

  if (isLoading) return <div className="client-tab-empty">Loading…</div>;
  if (counsel.length === 0) {
    return (
      <div className="cc-empty">
        <div className="icon" aria-hidden>
          ⌗
        </div>
        <EmptyCopy
          title="No opposing counsel recorded yet"
          mine="No opposing counsel on the matters assigned to you"
          body="When opposing counsel is linked to one of this client's matters, the record shows up here with their firm and contact info."
        />
      </div>
    );
  }
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Opposing counsel</div>
        <div className="cc-card-sub">· {counsel.length}</div>
      </div>
      <div>
        {counsel.map((oc) => (
          <div
            key={oc._id}
            className="row clickable"
            onClick={() => navigate(`/opposing/${oc._id}`)}
          >
            <div>
              <div className="row-title">
                <EntityLink kind="opposing" id={oc._id} stopPropagation subtle>
                  {oc.name}
                </EntityLink>
              </div>
              <div className="row-sub">{[oc.role, oc.firm].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <div className="row-meta">
              {oc.email ? (
                <a
                  href={`mailto:${oc.email}`}
                  className="cc-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {oc.email}
                </a>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
